import {apiClient} from '@common/http/query-client';
import {
  decodeBootstrapData,
  getBootstrapData,
  setBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {FirebaseOptions, getApp, getApps, initializeApp} from 'firebase/app';
import {
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

export type FirstPartyFirebaseProvider = 'google' | 'apple';

export function webFirebaseAuthenticationIsConfigured(): boolean {
  const options = firebaseOptions();
  return !!(
    options.apiKey &&
    options.authDomain &&
    options.appId &&
    options.projectId
  );
}

export async function signInWithWebFirebase(
  providerName: FirstPartyFirebaseProvider,
  minimumAgeConfirmed = false,
) {
  if (!webFirebaseAuthenticationIsConfigured()) {
    throw new Error('Firebase web authentication is not configured.');
  }

  const app = getApps().length
    ? getApp()
    : initializeApp(firebaseOptions() as FirebaseOptions);
  const auth = getAuth(app);
  const provider =
    providerName === 'google'
      ? new GoogleAuthProvider()
      : new OAuthProvider('apple.com');

  if (provider instanceof OAuthProvider) {
    provider.addScope('email');
    provider.addScope('name');
  } else {
    provider.setCustomParameters({prompt: 'select_account'});
  }

  const credential = await signInWithPopup(auth, provider);
  const idToken = await credential.user.getIdToken(true);
  const response = await apiClient.post<{bootstrap_data: string}>(
    'auth/firebase',
    {
      id_token: idToken,
      token_name: 'web-browser',
      display_name: credential.user.displayName || undefined,
      minimum_age_confirmed: minimumAgeConfirmed,
    },
  );
  const bootstrap = decodeBootstrapData(response.data.bootstrap_data);
  setBootstrapData(bootstrap);
  return bootstrap;
}

export async function signOutWebFirebase() {
  if (!webFirebaseAuthenticationIsConfigured() || !getApps().length) return;
  const auth = getAuth(getApp());
  if (auth.currentUser) {
    await signOut(auth);
  }
}

function firebaseOptions(): Record<string, string | undefined> {
  return getBootstrapData().firebase_app_check?.firebase || {};
}
